import * as THREE from 'three';
import { KestnerDeskRig, kestnerDeskPose } from '@/anim/kestnerDesk';
import type { ColliderHandle, RoomCapabilities, RoomEnvironment, RoomModule, RoomModuleDefinition } from '@/game/roomModule';
import type { RoomDiagnostics } from '@/game/roomDiagnostics';
import { findKestnerSeatedParts, KESTNER_SEATED_HIP, kestnerSeatedAsset } from '@/models/env/kestnerSeated';
import {
  createOffice, OFFICE_KESTNER_SEAT, OFFICE_LEDGER, OFFICE_SPAWN, OFFICE_WALL_MAP, OFFICE_ZONE_ID,
  officeAmbience, officeFixtures, officeSolids,
} from '@/world/office';
import { getZone } from '@/render/palette';
import { DEFAULT_OUT, tallyForOut } from '@/story/act3/tally';
import type { HallSolid } from '@/world/solids';

/**
 * Beat 11's office belongs behind a module boundary before stages add life to it:
 * stage 2 locates Kestner's seated figure and presentation clock here, while
 * stages 3–4 locate the desk and wall observe interactables here. Keeping the
 * shell's ownership here prevents those additions from reopening `main.ts`'s
 * room construction branch.
 */

/**
 * His shins and boots, as something she walks into rather than through.
 *
 * Only the part of him below her height needs one: the chair seat's collider
 * already stops at 0.405 m and she is 0.32 m, so she passes under the chair
 * and his thighs, and would otherwise have walked through his legs into the
 * kneehole. Knee at x −1.465 (hip −1.06 less the thigh's 0.405 reach), boots
 * 0.18 m further on, two legs 0.22 m apart about the seat's z. Not drawn — he
 * is — so it is added here rather than listed in `officeSolids()`, which is a
 * list of things that are both.
 */
const KESTNER_LEGS: HallSolid = {
  name: 'office-kestner-legs',
  half: { x: 0.15, y: 0.25, z: 0.22 },
  pos: { x: -1.51, y: 0.25, z: OFFICE_KESTNER_SEAT.z },
  colour: 0,
  kind: 'organic',
};

/**
 * Presentation time is clamped per frame so a tab that was hidden for a minute
 * does not come back and fast-forward him through the one gesture he makes.
 */
const MAX_FRAME = 0.1;

export const OFFICE_LEDGER_ID = 'office-ledger';
/** The wall of paper. `Q` held here raises HIS map instead of hers (`officeMap.ts`). */
export const OFFICE_WALL_MAP_ID = 'office-wall-map';

/**
 * `Q` at the desk: the page, read over his left shoulder.
 *
 * He faces −X, so his column reads along −Z, and the only lens that shows it
 * the right way up is on his side of the desk. The first composition sat on
 * Rosalie's side, south of the page, and the figures lay sideways in it, as a
 * row of bars at 16°. From behind his left shoulder they read left to right,
 * his hands are in the lamp's pool, and his face is behind the lens, which is
 * D-187's rule stated as a camera position. Presentation only: nothing reads
 * it, and the observe it belongs to changes nothing in the story.
 */
export const OFFICE_TALLY_SHOT = {
  position: { x: -1.20, y: 1.40, z: 0.22 },
  lookAt: { x: OFFICE_LEDGER.x - 0.05, y: OFFICE_LEDGER.top, z: OFFICE_LEDGER.z },
  fov: 30,
} as const;

export function createOfficeRoomModule(
  options: { part: number; dressing: boolean; out?: number },
  capabilities: RoomCapabilities,
): RoomModule {
  const office = createOffice({ dressing: options.dressing, tally: tallyForOut(options.out ?? DEFAULT_OUT) });
  const tally = office.getObjectByName('office-tally-merged');
  const tallyGlyphs = Array.isArray(tally?.userData['glyphs'])
    && tally.userData['glyphs'].every((glyph: unknown) => typeof glyph === 'string')
    ? [...tally.userData['glyphs']] as string[]
    : null;
  capabilities.scene.add(office);
  const colliders: ColliderHandle[] = [];
  for (const solid of officeSolids({ dressing: options.dressing })) {
    colliders.push(capabilities.colliders.add(solid));
  }

  // He sits on the chair, and the chair is dressing: `&dress=0` is the census
  // control and it has neither, so the budget delta counts him with the room.
  let kestner: { figure: THREE.Group; rig: KestnerDeskRig } | null = null;
  if (options.dressing) {
    const figure = kestnerSeatedAsset.create();
    figure.name = 'office-kestner';
    // Turned to face the desk (yaw −π/2), the asset's (x, z) lands at the
    // room's (−z, x) from its origin — so its hip, authored at z −0.205, sits
    // 0.205 m in +X of the origin, and the origin goes that far short of the seat.
    figure.position.set(OFFICE_KESTNER_SEAT.x + KESTNER_SEATED_HIP.z, 0, OFFICE_KESTNER_SEAT.z);
    figure.rotation.y = OFFICE_KESTNER_SEAT.yaw;
    capabilities.scene.add(figure);
    colliders.push(capabilities.colliders.add(KESTNER_LEGS));
    kestner = { figure, rig: new KestnerDeskRig(findKestnerSeatedParts(figure)) };
  }

  let clock = 0;
  let disposed = false;
  return {
    interactables: options.dressing ? [{
      id: OFFICE_LEDGER_ID,
      at: { x: OFFICE_LEDGER.x, y: OFFICE_LEDGER.top, z: OFFICE_LEDGER.z },
      verbs: ['observe'] as const,
      mapGlyph: 'book' as const,
      // Observe multiplies reach by three. At 0.25 m this gives her a 0.75 m
      // floor-level stand beside the desk, but excludes both the 1.4 m doorway
      // gap and the 3.9 m spawn gap that the first broad value accidentally hit.
      reach: 0.25,
      duration: 1.2,
    }, {
      id: OFFICE_WALL_MAP_ID,
      at: { x: OFFICE_WALL_MAP.x, y: OFFICE_WALL_MAP.y, z: OFFICE_WALL_MAP.z },
      verbs: ['observe'] as const,
      // ×3 for observe: 1.05 m. She stands a pace off the east wall; the
      // spawn gap (z 2.2) is 1.7 m away and out of it, so arriving is not
      // looking. Distance is horizontal, so the sheet's height does not count.
      reach: 0.35,
      duration: 1.2,
    }] : [],
    fixedStep: () => undefined,
    renderStep(frameDt): void {
      if (kestner === null) return;
      clock += Math.min(frameDt, MAX_FRAME);
      kestner.rig.apply(kestnerDeskPose(clock));
    },
    framing: (observingId) => observingId === OFFICE_LEDGER_ID ? OFFICE_TALLY_SHOT : null,
    diagnostics: (): RoomDiagnostics => ({
      kind: 'office',
      kestner: kestner === null ? null : { clock, glassesOff: kestner.rig.glassesOff },
      tallyGlyphs,
    }),
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const collider of colliders) capabilities.colliders.remove(collider);
      capabilities.scene.remove(office);
      if (kestner !== null) capabilities.scene.remove(kestner.figure);
    },
  };
}

/**
 * Beat 11 has no exterior light to inherit. The desk practical is the only
 * fixture; the key and fill merely stop the unlit shell becoming NaN-black.
 */
function officeEnvironment(): RoomEnvironment {
  const zone = getZone(OFFICE_ZONE_ID);
  if (!zone) throw new Error(`unknown zone "${OFFICE_ZONE_ID}"`);
  return {
    zone,
    lighting: {
      fixtures: officeFixtures(),
      keyDirection: { x: -0.4, y: 0.8, z: 0.4 },
      keyIntensity: 0.03,
      shadowExtent: 4,
      hemiIntensity: 0.04,
      ambientIntensity: 0.025,
      exposureReference: 1.2,
    },
    background: new THREE.Color(zone.shadow).multiplyScalar(0.08),
    spawn: OFFICE_SPAWN,
    ambience: officeAmbience(),
  };
}

export const OFFICE_ROOM_MODULE: RoomModuleDefinition = {
  room: 'office',
  environment: officeEnvironment,
  create: createOfficeRoomModule,
};
